<script lang="ts">
  import {
    faClone,
    faFileArrowDown,
    faFileImport,
    faFolderOpen,
    faPlus,
    faRotateRight,
    faTrashCan,
  } from '@fortawesome/free-solid-svg-icons';
  import { DRAWING_FILE_MIME } from '$lib/core/script';
  import { downloadText } from '$lib/services/download';
  import type { DocumentSession } from '../document-session.svelte';
  import type { EditorState } from '../editor-state.svelte';
  import { fieldKeys } from './field-keys';
  import Icon from './Icon.svelte';

  const { editor, session }: { editor: EditorState; session: DocumentSession } = $props();

  let open = $state(false);
  let picker = $state<HTMLInputElement>();

  async function download(id: string) {
    const file = await session.fileOf(id);
    if (file) downloadText(file.name, DRAWING_FILE_MIME, file.text);
  }

  async function importPicked(input: HTMLInputElement) {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    open = false;
    await session.importFile(await file.text());
  }

  function toggle() {
    open = !open;
    if (open) void session.refresh();
  }

  const savedLabel = $derived(
    session.savedAt ? new Date(session.savedAt).toLocaleTimeString() : 'not saved',
  );
</script>

<div class="bar">
  <div class="menu">
    <button
      type="button"
      class="toggle"
      title="Open, make, import or download your drawings"
      onclick={toggle}
      aria-expanded={open}
    >
      <Icon icon={faFolderOpen} />
      Drawings
    </button>

    {#if open}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div class="sheet" onmouseleave={() => (open = false)}>
        <div class="actions">
          <button type="button" class="new" onclick={() => session.create()}>
            <Icon icon={faPlus} />
            New drawing
          </button>
          <button type="button" class="new" onclick={() => picker?.click()}>
            <Icon icon={faFileImport} />
            Import drawing file…
          </button>
          <input
            bind:this={picker}
            type="file"
            accept=".json,application/json"
            aria-label="Import a drawing file"
            hidden
            onchange={(event) => importPicked(event.currentTarget)}
          />
        </div>
        <ul>
          {#each session.records as record (record.id)}
            <li class:active={record.id === editor.documentId}>
              <button type="button" class="open" onclick={() => session.open(record.id)}>
                <span class="name">{record.name}</span>
                <span class="when">{new Date(record.updatedAt).toLocaleString()}</span>
              </button>
              <button
                type="button"
                aria-label="Download {record.name}"
                title="Download drawing file"
                onclick={() => download(record.id)}
              >
                <Icon icon={faFileArrowDown} />
              </button>
              <button
                type="button"
                aria-label="Duplicate {record.name}"
                title="Duplicate"
                onclick={() => session.duplicate(record.id)}
              >
                <Icon icon={faClone} />
              </button>
              <button
                type="button"
                aria-label="Delete {record.name}"
                title="Delete"
                onclick={() => session.remove(record.id)}
              >
                <Icon icon={faTrashCan} />
              </button>
            </li>
          {/each}
        </ul>
        <!-- Below the list, so a reload ending under the pointer never moves a row out from
             under it, which would close the menu. -->
        {#if session.listState === 'loading'}
          <p class="status" role="status">Loading drawings…</p>
        {:else if session.listState === 'failed'}
          <p class="status failed" role="alert">
            Cannot load your drawings
            <button type="button" class="retry" onclick={() => session.refresh()}>
              <Icon icon={faRotateRight} />
              Retry
            </button>
          </p>
        {/if}
      </div>
    {/if}
  </div>

  <input
    class="title"
    type="text"
    value={editor.document.name}
    aria-label="Drawing name"
    onkeydown={fieldKeys(editor.document.name)}
    onchange={(event) => session.rename(event.currentTarget.value)}
  />

  {#if session.notice}
    <span class="saved notice" role="status">{session.notice}</span>
  {:else}
    <span class="saved">Saved {savedLabel}</span>
  {/if}
</div>

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .title {
    width: 14rem;
    padding: 5px 8px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: var(--color-text);
    font: inherit;
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .title:hover,
  .title:focus {
    border-color: var(--color-border);
    background: var(--color-bg);
    outline: none;
  }

  .menu {
    position: relative;
  }

  .sheet {
    position: absolute;
    z-index: 10;
    top: calc(100% + 6px);
    left: 0;
    width: 22rem;
    padding: var(--space-2);
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
    box-shadow: 0 12px 30px rgb(0 0 0 / 0.18);
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 16rem;
    overflow: auto;
  }

  li {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 2px;
    align-items: center;
  }

  li.active .name {
    color: var(--color-accent);
  }

  .open {
    display: grid;
    text-align: left;
    gap: 1px;
  }

  .when {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

  button {
    padding: 4px 8px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .menu > button,
  .new {
    border-color: var(--color-border);
    background: var(--color-surface);
  }

  .toggle,
  .new {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-2);
    margin-bottom: var(--space-2);
  }

  .new {
    justify-content: center;
  }

  .status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 var(--space-2);
    padding: 4px 8px;
    font-size: 0.8125rem;
    color: var(--color-text-muted);
  }

  .status.failed {
    color: var(--color-danger);
  }

  .retry {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    border-color: var(--color-border);
    background: var(--color-surface);
  }

  li button:hover {
    background: var(--color-bg);
  }

  /* One line that never wraps, so a long notice cannot change the header's height. */
  .saved {
    min-width: 0;
    overflow: hidden;
    font-size: 0.75rem;
    color: var(--color-text-muted);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .notice {
    color: var(--color-danger);
  }
</style>
